import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { namespace } from '@common-config/constants';
import { Code2, Columns2, Eye } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiMarkdown } from '@common-components/ai-chat/AiMarkdown';
import { muted } from './agent-inspect-shared';
import { SegmentedControl } from './SegmentedControl';

/**
 * A big, comfortable Markdown editor in a modal — for editing a long prompt or
 * knowledge base without fighting a small textarea. Three modes: write (raw
 * text), preview (rendered Markdown), or split (both side by side). The content
 * is plain Markdown text; `onSave` returns the edited text to the caller.
 *
 * Reused for the agent's identity prompt, its knowledge base, and knowledge files.
 */
export interface MarkdownEditorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** The current text. Loaded into the editor when the modal opens. */
  value: string;
  /** Called with the edited text when the user saves. */
  onSave: (value: string) => void;
  placeholder?: string;
  isRtl?: boolean;
}

type Mode = 'write' | 'preview' | 'split';

/** The rendered Markdown — the app's ONE Markdown renderer (`AiMarkdown`). */
const MarkdownPreview: React.FC<{ text: string; emptyText: string }> = ({ text, emptyText }) =>
  text.trim() === '' ? (
    <span className="text-sm" style={muted}>{emptyText}</span>
  ) : (
    <AiMarkdown text={text} className="text-sm leading-relaxed text-ai-text" />
  );

export const MarkdownEditorModal: React.FC<MarkdownEditorModalProps> = ({
  open,
  onOpenChange,
  title,
  value,
  onSave,
  placeholder,
  isRtl,
}) => {
  const { t } = useTranslation(namespace);
  const [draft, setDraft] = useState(value);
  const [mode, setMode] = useState<Mode>('split');

  // Load the text on OPEN only — a parent update while the editor is open (e.g.
  // the agent form reloading after an AI change) must not wipe the user's draft.
  useEffect(() => {
    if (open) setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = () => {
    onSave(draft);
    onOpenChange(false);
  };

  const paneLabel = (text: string) => (
    <div className="text-[11px] font-semibold uppercase tracking-wider mb-2" style={muted}>
      {text}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Near-fullscreen fixed-height shell (no outer scroll): pinned header + footer,
          the panes fill the remaining height and scroll individually. */}
      <DialogContent
        className="w-[96vw] sm:max-w-[1440px] h-[92vh] max-h-[92vh] p-0 sm:p-0 gap-0 overflow-hidden"
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        <DialogHeader
          className="shrink-0 flex flex-row flex-wrap items-center justify-between gap-3 space-y-0 px-6 py-4 pe-16 border-b"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <DialogTitle className="text-lg">{title}</DialogTitle>
          <SegmentedControl<Mode>
            value={mode}
            onChange={setMode}
            ariaLabel={title}
            testId="md-editor-mode"
            options={[
              { value: 'write', label: t('myBridge.mdEditor.write'), icon: Code2 },
              { value: 'split', label: t('myBridge.mdEditor.split'), icon: Columns2 },
              { value: 'preview', label: t('myBridge.mdEditor.preview'), icon: Eye },
            ]}
          />
        </DialogHeader>

        <div
          className={`flex-1 min-h-0 grid gap-5 px-6 py-5 ${mode === 'split' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}
        >
          {mode !== 'preview' && (
            <div className="flex flex-col min-h-0">
              {mode === 'split' && paneLabel(t('myBridge.mdEditor.markdown'))}
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    e.preventDefault();
                    save();
                  }
                }}
                placeholder={placeholder}
                // Typed: per its own script. Empty: the page direction (dir=auto would fall back to LTR).
                dir={draft ? 'auto' : isRtl ? 'rtl' : 'ltr'}
                className="flex-1 min-h-0 resize-none font-mono text-[14px] leading-7 p-5"
                style={{ height: '100%' }}
                data-testid="md-editor-textarea"
              />
            </div>
          )}
          {mode !== 'write' && (
            <div className="flex flex-col min-h-0">
              {mode === 'split' && paneLabel(t('myBridge.mdEditor.preview'))}
              <div
                className="flex-1 min-h-0 overflow-y-auto rounded-md border px-6 py-5"
                style={{ borderColor: 'var(--color-border)', background: 'var(--color-bg)' }}
              >
                <MarkdownPreview text={draft} emptyText={t('myBridge.mdEditor.empty')} />
              </div>
            </div>
          )}
        </div>

        <DialogFooter
          className="shrink-0 flex flex-row sm:flex-row items-center justify-between sm:justify-between gap-3 sm:space-x-0 px-6 py-4 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <span className="text-xs" style={muted}>
            {/* No `count` here: i18next would treat it as a plural selector for a
                key that has no plural forms. The label only interpolates `chars`. */}
            {t('myBridge.mdEditor.footer', { chars: draft.length.toLocaleString() })}
          </span>
          {/* No Cancel — the header X discards; Done is the single primary action. */}
          <Button
            className="min-w-[140px] h-10 px-6 font-semibold shadow-sm hover:opacity-90"
            style={{ background: 'var(--cta-button-bg)', color: 'var(--cta-button-text,#fff)' }}
            onClick={save}
            data-testid="md-editor-save"
          >
            {t('myBridge.mdEditor.done')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
